import React, { useEffect, useState } from 'react';
import { useStoreContext } from '../../Contexts/StoreContext';
import { orderTypeAsSlug, orderTypeAsSlugConvert, storeIsOpenByOrderType } from '../../Utils/Helpers';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import { ORDER_TYPE_LABELS } from '../../Utils/Constants/Service';
import Modal from './Modal';
import Confirm from './Confirm';
import { useTranslation } from 'react-i18next';
import CardStoreListDefault from './CardStoreListDefault';
import CardStoreListGoogleMaps from './CardStoreListGoogleMaps';

interface CardStoreListProps {
    store: any;
    onCardClick: (store: any) => void;
    enableCardClick?: boolean;
    showServices?: boolean;
    isCollapsed?: boolean;
    className?: string;
    orderType: any;
    isGoogleMaps?: boolean;
}

const CardStoreList = ({
    store,
    onCardClick,
    enableCardClick = true,
    showServices = false,
    isCollapsed = false,
    className,
    orderType,
    isGoogleMaps = false,
}: CardStoreListProps) => {

    const { t } = useTranslation();
    const { currentStore, setCurrentAddress } = useStoreContext();
    const { visibleOrderType, isMenu, nextOrderType } = useOrderTypeContext();
    const [isOpen, setIsOpen] = useState(false);
    const isCurrent = currentStore && currentStore.id === store.id;
    const [ showConfirmStoreNavigation, setShowConfirmStoreNavigation ] = useState<boolean>(false);

    const [ nextCurrentOrderType, setNextCurrentOrderType ] = useState<any>(null);
    const [ showInfo, setShowInfo ] = useState<boolean>(isCollapsed);

    const activeServices = store.store_services?.filter((service: any) =>
        service.active
        && visibleOrderType.some((type: any) => type.id === service.order_type))
        || [];

    const storeChanged = !isMenu && currentStore && currentStore.id !== store.id;

    const hasOrderTypeChange = (orderTypeNameSelected: string) => {
        return orderTypeAsSlugConvert(orderTypeNameSelected) !== orderType;
    }

    const isCurrentOrderType = (orderTypeNameSelected: string) => {
        return orderType ? orderTypeAsSlugConvert(orderTypeNameSelected) === orderType : false;
    }

    const hasStoreOrServiceChange = (orderTypeNameSelected: string) => {
        if (orderType && (storeChanged || hasOrderTypeChange(orderTypeNameSelected))) {
          setShowConfirmStoreNavigation(true);
          return true;
        }
        return false;
    };

    const handleClickService = (orderTypeSelected: any) => {
        setNextCurrentOrderType(orderTypeSelected);
        setTimeout(() => {
            if (hasStoreOrServiceChange(orderTypeSelected.order_type_name)) return;
            window.location.href = `/${orderTypeAsSlug(orderTypeSelected.order_type_name)}/${store.slug}`;
            handleStoreNavigation(orderTypeSelected.order_type_name);
        }, 100);
    }

    const handleStoreNavigation = (orderType: any) => {
        setCurrentAddress(null);
        window.location.href = `/${orderTypeAsSlug(orderType)}/${store.slug}`;
    }

    useEffect(() => {
        setIsOpen(storeIsOpenByOrderType(nextOrderType || orderType, store));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderType, store]);

    const getMessageChangeStoreOrService = () => {

        const isServiceChanged = nextCurrentOrderType && orderTypeAsSlugConvert(nextCurrentOrderType.order_type_name) !== orderType;

        const serviceLabelFrom = ORDER_TYPE_LABELS[orderType as keyof typeof ORDER_TYPE_LABELS];

        const serviceLabelTo = nextCurrentOrderType
            ? ORDER_TYPE_LABELS[orderTypeAsSlugConvert(nextCurrentOrderType.order_type_name) as keyof typeof ORDER_TYPE_LABELS]
            : '';

        const getChangeMessage = () => {
                if (storeChanged && isServiceChanged) {
                    return t('layout.store_list.message_change_store_or_service.store_and_service_changed');
                }

                if (storeChanged) {
                    return t('layout.store_list.message_change_store_or_service.store_changed');
                }

                if (isServiceChanged) {
                    return t('layout.store_list.message_change_store_or_service.service_changed');
                }

                return '';
            };

            const getStoreChangeInfo = () => {
                if (!storeChanged) return '';
                return `${currentStore.name} → <strong>${store.name}</strong>`;
            };

            const getServiceChangeInfo = () => {
                if (!isServiceChanged) return '';
                return `<strong>${serviceLabelFrom}</strong> → ${serviceLabelTo}`;
            };

            return `
                ${getChangeMessage()}
                ${getStoreChangeInfo()}
                ${(storeChanged && isServiceChanged) ? '<br />' : ''}
                ${getServiceChangeInfo()}
                <br /><br />
                ${t('layout.store_list.message_change_store_or_service.text_1')}
            `;
    };

    const handleToggleShowInfo = () => {
        setShowInfo(!showInfo);
    }

    return (
        <>
            {isGoogleMaps
                ?   <CardStoreListGoogleMaps
                        store={store}
                        onCardClick={() => onCardClick(store)}
                        enableCardClick={enableCardClick} // && activeServices?.length === 0
                        showServices={showServices}
                        activeServices={activeServices}
                        isCollapsed={isCollapsed}
                        className={className}
                        isOpen={isOpen}
                        isCurrent={isCurrent}
                        isCurrentOrderType={isCurrentOrderType}
                        showInfo={showInfo}
                        onToggleShowInfo={handleToggleShowInfo}
                        onClickService={(e: any) => handleClickService(e)}
                    />
                :
                    <CardStoreListDefault
                        store={store}
                        onCardClick={() => onCardClick(store)}
                        enableCardClick={enableCardClick} // || activeServices?.length === 0
                        showServices={showServices}
                        activeServices={activeServices}
                        isCollapsed={isCollapsed}
                        className={className}
                        isOpen={isOpen}
                        isCurrent={isCurrent}
                        isCurrentOrderType={isCurrentOrderType}
                        showInfo={showInfo}
                        onToggleShowInfo={handleToggleShowInfo}
                        onClickService={(e: any) => handleClickService(e)}
                    />
            }

            <Modal open={showConfirmStoreNavigation} className="max-w-md m-4" onClose={() => setShowConfirmStoreNavigation(false)}>
                <Confirm
                    onConfirm={() => handleStoreNavigation(nextCurrentOrderType.order_type_name)}
                    onCancel={() => setShowConfirmStoreNavigation(false)}
                    title={t('layout.store_list.confirm_change_title')}
                    message={getMessageChangeStoreOrService()}
                    labelConfirm={t('layout.store_list.confirm_button_label')}
                />
            </Modal>
        </>
    );
}

export default CardStoreList;
